import { http } from '../request'
import {
  expectArrayData,
  normalizeStatusText,
  type BulkActionResult
} from './shared'

export interface GradeDto {
  id: number
  name: string
  level?: number
  year?: number
  status: number
  /** 年级全局顺序号（后端系统按「学段配置顺序→学段内年级号」维护，用于排序；非手动排序号） */
  sort?: number
  stageConfigId?: number
  stageName?: string
  displayName?: string
  cohortName?: string
  lifecycleStatus?: string
  studyYears?: number
  remark?: string
}

type GradePayload = {
  stageConfigId: number | undefined
  year: number | undefined
  level: number | undefined
  status: string
  remark: unknown
}

function toGradePayload(payload: unknown): GradePayload {
  const grade = payload as Partial<GradeDto> & Record<string, unknown>
  return {
    stageConfigId: grade.stageConfigId != null ? Number(grade.stageConfigId) : undefined,
    year: grade.year != null ? Number(grade.year) : undefined,
    level: grade.level != null ? Number(grade.level) : undefined,
    status: normalizeStatusText(grade.status),
    remark: grade.remark
  }
}

export interface GraduationPreviewItem {
  gradeId: number
  gradeName: string
  enabledClassCount: number
  subgroupCount: number
  activeStudentCount: number
}

export interface GraduationPreview {
  promotedGrades: GraduationPreviewItem[]
  graduatedGrades: GraduationPreviewItem[]
  notEnrolledGrades: GraduationPreviewItem[]
}

export interface GradeLevelPreview {
  recommendedLevel: number
  displayName?: string
  basis: 'ACADEMIC_YEAR' | 'ANCHOR' | 'DEFAULT'
  basisMessage: string
  anchorYear?: number | null
  anchorLevel?: number | null
  gradeLabels: string[]
  studyYears?: number | null
  lifecycleStatus: 'IN_SCHOOL' | 'NOT_ENROLLED' | 'GRADUATED'
  outsideStudyRange: boolean
}

export const gradeApi = {
  /**
   * 年级列表。缺省返回全量（含已归档，供回读/管理场景）；
   * 操作场景（新排课/分班/选课/考试等）请传 `status: '启用'` 隐藏归档组织。
   */
  async list(status?: '启用' | '归档'): Promise<GradeDto[]> {
    const response = await http.get<GradeDto[]>('/academic/grades', status ? { status } : undefined)
    return expectArrayData<GradeDto>(response.data, '/academic/grades')
  },
  async previewLevel(stageConfigId: number, year: number): Promise<GradeLevelPreview> {
    const response = await http.get<GradeLevelPreview>(
      '/academic/grades/preview-level',
      { stageConfigId, year }
    )
    return response.data
  },
  async create(payload: Partial<GradeDto>): Promise<GradeDto> {
    const response = await http.post<GradeDto>('/academic/grades', toGradePayload(payload))
    return response.data
  },
  async update(id: number, payload: Partial<GradeDto>): Promise<GradeDto> {
    const response = await http.put<GradeDto>(`/academic/grades/${id}`, toGradePayload(payload))
    return response.data
  },
  async remove(id: number): Promise<void> {
    await http.delete<void>(`/academic/grades/${id}`)
  },
  /** 删除届别（含子树逐项判定）：返回逐项处置结果。 */
  async removeWithSubtree(id: number): Promise<BulkActionResult> {
    const response = await http.delete<BulkActionResult>(`/academic/grades/${id}/with-subtree`)
    return response.data
  },
  async previewGraduation(): Promise<GraduationPreview> {
    const response = await http.get<GraduationPreview>('/academic/grades/graduation-preview')
    return {
      promotedGrades: response.data?.promotedGrades ?? [],
      graduatedGrades: response.data?.graduatedGrades ?? [],
      notEnrolledGrades: response.data?.notEnrolledGrades ?? []
    }
  },
  async sealGraduation(id: number): Promise<GraduationPreviewItem> {
    const response = await http.post<GraduationPreviewItem>(`/academic/grades/${id}/seal-graduation`)
    return response.data
  }
}
